import {
    createGrn,
    getAvailablePOs,
    getAvailableSTNs,
    getGrnPrefill,
    type AvailableStn,
    type GrnPrefillItem,
    type GrnSourceType,
} from '@api/warehouse-ops';
import { listWarehousesForPicker, type WarehousePickItem } from '@api/work-order';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import useToaster from '@hooks/toaster/useToaster';
import { Button, Group, Modal, Select, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { Plus } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useNavigate } from 'react-router';

import styles from '../../vendorReceive/ReceiveGrid.module.css';
import { receiveFieldClasses, useEnteredByName } from '../../vendorReceive/receiveForm';

import {
    buildFgLines,
    canRecordFg,
    fgDifference,
    fgLineIssues,
    fillAllRemaining,
    toGrnItems,
    type FgLine,
} from './fgLines';

type Props = {
    opened: boolean;
    onClose: () => void;
    /** A transfer to open with, from Mark received or Outbound's Create GRN. */
    prefill?: { sourceType: GrnSourceType; sourceId: string } | null;
};

/** `PO:<number>` or `STN:<id>`: one picker for both, as on the RM/PM receipt. */
type DocumentKey = string;
const keyOf = (sourceType: GrnSourceType, sourceId: string): DocumentKey =>
    `${sourceType === 'STN' ? 'STN' : 'PO'}:${sourceId}`;
const splitKey = (key: DocumentKey): { sourceType: GrnSourceType; sourceId: string } | null => {
    const at = key.indexOf(':');
    if (at < 0) return null;
    return { sourceType: key.slice(0, at) === 'STN' ? 'STN' : 'PURCHASE_ORDER', sourceId: key.slice(at + 1) };
};

const today = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const formatQty = (value: number) => value.toLocaleString('en-IN', { maximumFractionDigits: 3 });

/** The server's own reason (a transfer received at the wrong warehouse, say), else a fallback. */
const errorText = (error: unknown): string => {
    const e = error as { error?: { message?: string }; message?: string } | null;
    return e?.error?.message || e?.message || 'Please retry.';
};

/**
 * Add GRN for finished goods, laid out as the RM/PM receipt is: what the goods came on, the
 * delivery note, when and who, then the lines as a grid and the button below it. What differs
 * is the engine behind it, not the form: a finished-goods GRN is checked and put away as later
 * steps on the GRN, and one against a WMS-run PO is not booked twice.
 */
const FgReceiveModal: FC<Props> = ({ opened, onClose, prefill }) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { showToastSuccess } = useToaster();

    const [documentKey, setDocumentKey] = useState<DocumentKey>('');
    const [warehouseId, setWarehouseId] = useState('');
    const [receivedDate, setReceivedDate] = useState('');
    const [reference, setReference] = useState('');
    const [enteredBy, setEnteredBy] = useEnteredByName();
    const [lines, setLines] = useState<FgLine[]>([]);
    const [serverError, setServerError] = useState<string | null>(null);

    const source = splitKey(documentKey);

    const poQuery = useQuery<Array<{ id: string; label: string }>>({
        queryKey: ['available-pos'],
        queryFn: getAvailablePOs,
        enabled: opened,
        staleTime: 30 * 1000,
    });
    const stnQuery = useQuery<AvailableStn[]>({
        queryKey: ['available-stns'],
        queryFn: getAvailableSTNs,
        enabled: opened,
        staleTime: 30 * 1000,
    });
    const warehousesQuery = useQuery<WarehousePickItem[]>({
        queryKey: ['warehouses-picker-internal'],
        queryFn: () => listWarehousesForPicker(),
        enabled: opened,
    });
    const prefillQuery = useQuery<GrnPrefillItem[]>({
        queryKey: ['grn-prefill', source?.sourceType, source?.sourceId],
        queryFn: () => (source ? getGrnPrefill(source.sourceType, source.sourceId) : Promise.resolve([])),
        enabled: opened && !!source,
        retry: false,
    });

    // Opened from a transfer: start on it.
    useEffect(() => {
        if (opened && prefill) setDocumentKey(keyOf(prefill.sourceType, prefill.sourceId));
    }, [prefill, opened]);

    // A different document is a different delivery: its lines replace the last one's.
    useEffect(() => {
        setLines(prefillQuery.data ? buildFgLines(prefillQuery.data) : []);
    }, [prefillQuery.data]);

    /*
     * A transfer lands as what it carries, at the warehouse it was sent to: once it is picked the
     * receiving warehouse is that one and stays put. The server holds a GRN to the same.
     */
    const pickedStn = source?.sourceType === 'STN' ? stnQuery.data?.find((s) => s.id === source.sourceId) : undefined;
    const lockedWarehouseId =
        pickedStn && (warehousesQuery.data ?? []).some((w) => w.warehouseId === pickedStn.toNodeId)
            ? pickedStn.toNodeId
            : null;
    useEffect(() => {
        if (lockedWarehouseId) setWarehouseId(lockedWarehouseId);
    }, [lockedWarehouseId]);

    const documents = useMemo(
        () =>
            [
                {
                    group: 'Purchase orders',
                    items: (poQuery.data ?? []).map((po) => ({ value: keyOf('PURCHASE_ORDER', po.id), label: po.label })),
                },
                {
                    group: 'Stock transfers',
                    items: (stnQuery.data ?? []).map((stn) => ({
                        value: keyOf('STN', stn.id),
                        label: `${stn.label} · from ${stn.fromName}`,
                    })),
                },
            ].filter((group) => group.items.length > 0),
        [poQuery.data, stnQuery.data],
    );
    const documentsLoading = poQuery.isLoading || stnQuery.isLoading;

    const issuesByItem = useMemo(() => new Map(lines.map((line) => [line.itemId, fgLineIssues(line)])), [lines]);
    const problems = lines.filter((line) => (issuesByItem.get(line.itemId)?.length ?? 0) > 0);
    const entered = lines.filter((line) => line.quantity.trim() !== '').length;
    const canSubmit = !!source && !!warehouseId && enteredBy.trim().length > 0 && canRecordFg(lines);

    const updateLine = (itemId: string, quantity: string) =>
        setLines((current) => current.map((line) => (line.itemId === itemId ? { ...line, quantity } : line)));

    const handleClose = () => {
        setDocumentKey('');
        setWarehouseId('');
        setReceivedDate('');
        setReference('');
        setLines([]);
        setServerError(null);
        onClose();
    };

    const mutation = useMutation({
        mutationFn: () => {
            if (!source) throw new Error('Pick the PO or transfer the goods came on.');
            return createGrn({
                sourceWarehouseId: warehouseId,
                sourceType: source.sourceType,
                sourceId: source.sourceId,
                // Finished goods only: raw and packing material has its own receipt, with QC.
                itemType: 'product',
                receivedDate: receivedDate || today(),
                reference: reference.trim() || undefined,
                receivedBy: enteredBy.trim() || undefined,
                items: toGrnItems(lines),
            });
        },
        onMutate: () => setServerError(null),
        onSuccess: (grn) => {
            void queryClient.invalidateQueries({ queryKey: ['grns'] });
            void queryClient.invalidateQueries({ queryKey: ['available-pos'] });
            void queryClient.invalidateQueries({ queryKey: ['available-stns'] });
            void queryClient.invalidateQueries({ queryKey: ['stns'] });
            showToastSuccess({ title: `${grn.code} recorded` });
            handleClose();
            void navigate('/warehouse-ops/inbound');
        },
        onError: (error) => setServerError(errorText(error)),
    });

    const orderedHeader = source?.sourceType === 'STN' ? 'Dispatched' : 'Ordered';
    const columns: DataSheetColumn<FgLine>[] = [
        {
            key: 'itemName',
            header: 'Item',
            width: 320,
            sticky: true,
            render: (_value, line) => (
                <div>
                    <Text fz={13} fw={500} c={neutralColors.textPrimary}>{line.itemName}</Text>
                    {line.sku && line.sku !== line.itemName && (
                        <Text fz={12} c={neutralColors.textMuted}>{line.sku}</Text>
                    )}
                </div>
            ),
        },
        {
            key: 'quantity',
            header: 'Quantity',
            width: 150,
            sticky: true,
            render: (_value, line) => (
                <Group gap={6} wrap="nowrap">
                    <TextInput
                        size="xs"
                        classNames={{ input: styles.cellInput }}
                        placeholder="0"
                        value={line.quantity}
                        onChange={(e) => updateLine(line.itemId, e.currentTarget.value)}
                        w={90}
                        error={(issuesByItem.get(line.itemId)?.length ?? 0) > 0}
                    />
                    {line.unit && <Text fz={12} c={neutralColors.textMuted}>{line.unit}</Text>}
                </Group>
            ),
        },
        {
            key: 'ordered',
            header: orderedHeader,
            width: 130,
            align: 'right',
            render: (_value, line) => formatQty(line.ordered),
        },
        {
            key: 'difference',
            header: 'Difference',
            width: 130,
            align: 'right',
            render: (_value, line) => {
                const difference = fgDifference(line);
                if (difference === null) return '—';
                return (
                    <Text
                        fz={13}
                        c={difference === 0 ? neutralColors.textSecondary : difference > 0 ? semanticColors.positive.dark : semanticColors.warning.dark}
                    >
                        {difference > 0 ? '+' : ''}{formatQty(difference)}
                    </Text>
                );
            },
        },
    ];

    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            size="90vw"
            centered
            closeOnClickOutside={false}
            title={
                <Text fz={16} fw={600}>
                    Add GRN · Finished goods
                </Text>
            }
        >
            <div className="flex w-full flex-col gap-4">
                {serverError && (
                    <div className={`${styles.banner} ${styles.bannerCritical}`}>
                        <div className={styles.bannerText}>
                            <Text fz={13} fw={600} c={neutralColors.textPrimary}>Nothing was recorded</Text>
                            <Text fz={12} c={neutralColors.textSecondary}>{serverError}</Text>
                        </div>
                    </div>
                )}

                <Group gap={16} align="flex-end">
                    <Select
                        comboboxProps={{ position: 'bottom-start' }}
                        classNames={receiveFieldClasses}
                        label="Received against"
                        description={pickedStn ? `From ${pickedStn.fromName} (stock transfer)` : 'The PO or stock transfer the goods came on'}
                        placeholder={documentsLoading ? 'Loading…' : 'Pick the PO or transfer'}
                        data={documents}
                        value={documentKey || null}
                        onChange={(value) => setDocumentKey(value ?? '')}
                        searchable
                        nothingFoundMessage="Nothing open with anything left to receive"
                        withAsterisk
                        w={320}
                    />
                    <Select
                        comboboxProps={{ position: 'bottom-start' }}
                        classNames={receiveFieldClasses}
                        label="Receiving warehouse"
                        description={lockedWarehouseId ? 'Where this transfer was sent' : 'Where the goods arrived'}
                        placeholder="Pick the warehouse"
                        data={(warehousesQuery.data ?? []).map((w) => ({ value: w.warehouseId, label: w.name }))}
                        value={warehouseId || null}
                        onChange={(value) => setWarehouseId(value ?? '')}
                        disabled={!!lockedWarehouseId}
                        searchable
                        withAsterisk
                        w={260}
                    />
                    <TextInput
                        classNames={receiveFieldClasses}
                        label="Delivery note"
                        description="The challan it came on"
                        placeholder="CHLN-1234"
                        value={reference}
                        onChange={(e) => setReference(e.currentTarget.value)}
                        w={200}
                    />
                    <DatePickerInput
                        classNames={receiveFieldClasses}
                        label="Received on"
                        description="Leave blank for today"
                        placeholder="Today"
                        valueFormat="DD MMM YYYY"
                        value={receivedDate || null}
                        onChange={(value) => setReceivedDate(value ?? '')}
                        clearable
                        w={180}
                    />
                    <TextInput
                        classNames={receiveFieldClasses}
                        label="Received by"
                        description="Who received the goods"
                        placeholder="Your name"
                        value={enteredBy}
                        onChange={(e) => setEnteredBy(e.currentTarget.value)}
                        withAsterisk
                        w={220}
                    />
                </Group>

                {!source && !documentsLoading && (
                    <Text fz={13} c={neutralColors.textSecondary}>
                        {documents.length === 0
                            ? 'There is no open purchase order or stock transfer with anything left to receive, so there is nothing to record against yet.'
                            : 'Pick the purchase order or stock transfer this delivery came on. Its lines appear below.'}
                    </Text>
                )}
                {source && prefillQuery.isError && (
                    <Text fz={13} c={semanticColors.critical.dark}>Could not load the lines of this document. Pick it again, or retry.</Text>
                )}

                <Text fz={13} fw={500} c={neutralColors.textPrimary}>
                    Enter what arrived on this delivery. Quality checks and putaway follow on the GRN once it is recorded.
                </Text>

                <DataSheet<FgLine>
                    data={lines}
                    columns={columns}
                    title="Delivery lines"
                    subtitle={
                        !source
                            ? 'The lines of the PO or transfer appear here once it is picked.'
                            : prefillQuery.isLoading
                              ? 'Loading lines…'
                              : `${entered} of ${lines.length} line${lines.length === 1 ? '' : 's'} entered`
                    }
                    getRowKey={(line) => line.itemId}
                    getRowClassName={(line) => ((issuesByItem.get(line.itemId)?.length ?? 0) > 0 ? styles.rowProblem : '')}
                    rowHeight={48}
                    maxHeight="auto"
                    toolbarControls={
                        <Group gap={8} className={styles.toolbar}>
                            {problems.length > 0 && (
                                <Text fz={12} fw={600} c={semanticColors.critical.dark}>
                                    {problems.length} row{problems.length === 1 ? '' : 's'} to fix
                                </Text>
                            )}
                            <Button
                                variant={ButtonVariant.DEFAULT}
                                size="xs"
                                onClick={() => setLines(fillAllRemaining)}
                                disabled={lines.length === 0}
                            >
                                Add all remaining items
                            </Button>
                        </Group>
                    }
                />

                {problems.length > 0 && (
                    <div className={`${styles.banner} ${styles.bannerCritical} ${styles.problemList}`}>
                        <div className={styles.bannerText}>
                            <Text fz={13} fw={600} c={semanticColors.critical.dark}>
                                {problems.length} row{problems.length === 1 ? '' : 's'} to fix before this delivery can be saved
                            </Text>
                            {problems.map((line) => (
                                <Text key={line.itemId} fz={12} c={neutralColors.textPrimary}>
                                    <Text span fz={12} fw={600}>{line.itemName}:</Text> {issuesByItem.get(line.itemId)?.join(' ')}
                                </Text>
                            ))}
                        </div>
                    </div>
                )}

                <div className={styles.submitBar}>
                    <Text fz={12} c={neutralColors.textMuted}>
                        A line left blank is recorded as nothing received. The whole delivery goes in together, or none of it does.
                    </Text>
                    <Group gap={12} wrap="nowrap">
                        {problems.length > 0 && (
                            <Text fz={12} fw={600} c={semanticColors.critical.dark}>
                                Fix {problems.length} row{problems.length === 1 ? '' : 's'} to save
                            </Text>
                        )}
                        <Button
                            variant={ButtonVariant.ACTION}
                            leftSection={<Plus size={14} />}
                            onClick={() => mutation.mutate()}
                            disabled={!canSubmit}
                            loading={mutation.isPending}
                        >
                            Record delivery
                        </Button>
                    </Group>
                </div>
            </div>
        </Modal>
    );
};

export default FgReceiveModal;
